import React from 'react';
import { Link } from 'react-router-dom';
import { Award, GraduationCap, Globe, Briefcase, Users, TrendingUp, ArrowRight, Mail, Linkedin, Send } from 'lucide-react';
import { founderProfile, globalRequests } from '../data/mock';
import { RequestDialog } from '../components/RequestDialog';

export const About = () => {
  const [isRequestOpen, setIsRequestOpen] = React.useState(false);
  const [activeConfig, setActiveConfig] = React.useState({});

  const handleConsultation = (source = "General") => {
    setActiveConfig(globalRequests.consultation);
    setIsRequestOpen(true);
  };

  return (
    <div className="min-h-screen bg-black pt-20">
      <RequestDialog
        isOpen={isRequestOpen}
        onOpenChange={setIsRequestOpen}
        config={activeConfig}
        context="About Page"
      />
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-b from-black to-gray-900 overflow-hidden">
        {/* Background Accents */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gold/5 blur-[120px] rounded-full translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gold/5 blur-[100px] rounded-full -translate-x-1/2 translate-y-1/2"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-center gap-16 lg:gap-24">

            {/* Subtle Photo Section */}
            <div className="relative order-2 lg:order-1 flex-shrink-0 group">
              {/* Decorative Glow */}
              <div className="absolute -inset-4 bg-gradient-to-br from-gold/20 via-transparent to-transparent blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

              <div className="relative overflow-hidden rounded-2xl border border-gold/20 bg-black p-3 backdrop-blur-xl shadow-2xl w-full max-w-[320px] mx-auto lg:mx-0">
                <img
                  src={founderProfile.image}
                  alt={founderProfile.name}
                  className="w-full aspect-[4/5] object-cover object-top rounded-xl transition-all duration-700 ease-in-out"
                />

                {/* Image Frame Accent */}
                <div className="absolute top-0 right-0 w-16 h-16 border-t border-r border-gold/30 rounded-tr-xl m-3"></div>
                <div className="absolute bottom-0 left-0 w-16 h-16 border-b border-l border-gold/30 rounded-bl-xl m-3"></div>
              </div>

              {/* Refined Enrollment Info */}
              <div className="mt-6 flex flex-col items-center lg:items-start">
                <div className="w-12 h-px bg-gold/10 mb-4"></div>
                <div className="bg-gold/5 border border-gold/10 px-3 py-1.5 rounded-lg">
                  <p className="text-gold text-[10px] font-bold uppercase tracking-[0.15em] font-serif leading-relaxed text-center lg:text-left">
                    {founderProfile.enrollment}
                  </p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 flex-1 text-center lg:text-left">
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 mb-6">
                <div className="inline-block px-4 py-1 bg-gold/10 border border-gold/30 rounded-full">
                  <span className="text-gold text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em]">Founding Partner</span>
                </div>
              </div>

              <h1 className="text-5xl lg:text-7xl font-serif font-bold text-white mb-6 tracking-tight leading-tight">
                {founderProfile.name}
              </h1>

              <div className="w-16 h-1 bg-gold mb-12 mx-auto lg:ml-0 opacity-80"></div>

              <p className="text-gray-200 text-lg sm:text-xl leading-relaxed mb-12 max-w-2xl font-normal text-center lg:text-left">
                {founderProfile.bio}
              </p>

              {/* Social Links Cards */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4">
                <a
                  href={`mailto:${founderProfile.email}`}
                  className="px-8 py-3 bg-gradient-to-br from-gray-900 to-black border border-gold/10 text-gold-light rounded-lg text-sm font-semibold hover:border-gold/40 hover:bg-gold/5 transition-all duration-300 transform hover:-translate-y-1 flex items-center group shadow-xl"
                >
                  <Mail size={16} className="mr-3 text-gold group-hover:scale-110 transition-transform" />
                  Email
                </a>
                <a
                  href={founderProfile.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-8 py-3 bg-gradient-to-br from-gray-900 to-black border border-gold/10 text-gold-light rounded-lg text-sm font-semibold hover:border-gold/40 hover:bg-gold/5 transition-all duration-300 transform hover:-translate-y-1 flex items-center group shadow-xl"
                >
                  <Linkedin size={16} className="mr-3 text-gold group-hover:scale-110 transition-transform" />
                  LinkedIn
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              Areas of <span className="text-gold">Expertise</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {founderProfile.expertiseHome.map((exp, idx) => (
              <div
                key={idx}
                className="group relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all duration-300"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                <div className="relative z-10 flex items-start">
                  <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                    <TrendingUp size={20} className="text-gold" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white group-hover:text-gold transition-colors duration-300">
                      {exp}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              <span className="text-gold">Education</span> & Credentials
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {founderProfile.education.map((edu, idx) => (
              <div
                key={idx}
                className="relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-8 hover:border-gold/50 transition-all duration-300 group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-gold/20 transition-colors duration-300">
                    <GraduationCap size={28} className="text-gold" />
                  </div>

                  <h3 className="text-2xl font-bold text-white mb-2">{edu.degree}</h3>
                  <p className="text-gold font-semibold mb-2">{edu.institution}</p>
                  {edu.honor && (
                    <p className="text-gray-400 text-sm mb-3 italic">{edu.honor}</p>
                  )}
                  <p className="text-gray-500 text-sm">{edu.year}</p>
                </div>
              </div>
            ))}
          </div>

          {/* <div className="text-center mt-8">
            <div className="inline-flex items-center px-6 py-3 bg-gold/10 border border-gold/30 rounded-lg">
              <Award size={20} className="text-gold mr-3" />
              <span className="text-gray-300">{founderProfile.enrollment}</span>
            </div>
          </div> */}
        </div>
      </section>

      {/* Professional Experience */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              Prior <span className="text-gold">Experience</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              {/* Built on a foundation of excellence at India's leading law firms */}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {founderProfile.priorExperience.map((firm, idx) => (
              <div
                key={idx}
                className="relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-8 text-center hover:border-gold/50 transition-all duration-300 group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                <div className="relative z-10">
                  <div className="w-12 h-12 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-gold/20 transition-colors duration-300">
                    <Briefcase size={24} className="text-gold" />
                  </div>
                  <h3 className="text-lg font-semibold text-white group-hover:text-gold transition-colors duration-300">
                    {firm}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Industry Contribution */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              Industry <span className="text-gold">Contribution</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {founderProfile.industryContribution.map((contribution, idx) => (
              <div
                key={idx}
                className="relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-xl p-6 hover:border-gold/50 transition-all duration-300 group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl"></div>

                <div className="relative z-10 flex items-start">
                  <div className="w-10 h-10 bg-gold/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-gold/20 transition-colors duration-300">
                    <Users size={20} className="text-gold" />
                  </div>
                  <p className="text-gray-300 leading-relaxed">{contribution}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* Global Exposure */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-4">
              <span className="text-gold">Global</span> Exposure
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="relative bg-gradient-to-br from-gray-900 to-black border border-gold/20 rounded-2xl p-10">
              <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent rounded-2xl"></div>

              <div className="relative z-10">
                <div className="flex items-center justify-center mb-8">
                  <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center">
                    <Globe size={36} className="text-gold" />
                  </div>
                </div>

                <p className="text-gray-300 text-lg text-center leading-relaxed mb-8">
                  {founderProfile.globalExposure.description}
                </p>

                {/* <div className="flex flex-wrap justify-center gap-3">
                  {founderProfile.globalExposure.jurisdictions.map((jurisdiction, idx) => (
                    <span
                      key={idx}
                      className="px-4 py-2 bg-gold/10 border border-gold/30 text-gray-300 rounded-full text-sm"
                    >
                      {jurisdiction}
                    </span>
                  ))}
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-serif font-bold text-white mb-6">
            Let's Work <span className="text-gold">Together</span>
          </h2>

          <p className="text-xl text-gray-300 mb-8 leading-relaxed">
            Reach out to discuss your legal needs and discover how LEXCEED can help you exceed benchmarks.
          </p>

          <button
            onClick={handleConsultation}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-gold to-gold-light text-black font-bold text-lg rounded-lg hover:shadow-2xl hover:shadow-gold/40 transform hover:scale-105 transition-all duration-300"
          >
            Schedule a Consultation
            <ArrowRight size={24} className="ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
};
